/**
 * Domain model — the single source of truth shared by the API and the web app.
 *
 * Every entity is declared once, as a Zod schema, and its TypeScript type is
 * inferred from it. The API validates at the edge with these schemas; the web
 * app imports the inferred types. A change here is a compile error on both
 * sides, which is exactly what we want from a contract.
 */
import { z } from 'zod';
/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */
/** Unix epoch milliseconds. SQLite stores these as INTEGER. */
export const Millis = z.number().int().nonnegative();
/* -------------------------------------------------------------------------- */
/* Models, effort, permissions                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Model aliases understood by the Claude CLI. We deliberately keep aliases
 * rather than pinned ids so a subscription user always lands on the current
 * generation, and we allow an arbitrary string for explicit pinning.
 *
 * `fable` was missing here while the learner carried two fable arms and could
 * serve it on any Auto run — so the bandit could reach a model no picker in
 * the product offered, and an operator who saw it in Analytics could not ask
 * for it. That is the wrong way round for a system whose first law is that
 * every learned decision is readable and overridable. `bandit.test.ts` derives
 * the check from `DEFAULT_ARMS`, so a ninth arm on a new model fails on the
 * day it is added rather than at the next time somebody looks.
 */
export const ModelAlias = z.enum(['default', 'fable', 'opus', 'sonnet', 'haiku', 'opusplan']);
export const ModelSelector = z.union([ModelAlias, z.string().min(1).max(120)]);
export const EffortLevel = z.enum(['low', 'medium', 'high', 'xhigh', 'max']);
/**
 * Metaclaude's own sentinel for "let the learner choose", spelled as the CLI's
 * `default` alias because that is also what we pass when nothing is pinned.
 *
 * It is not a model, and the distinction is load-bearing in two places that
 * used to disagree. The composer offers it as "Auto" and sends it like any
 * other choice, so `overrides.model` was *defined* on every message a person
 * ever typed; `choosePolicy` gated the bandit on `!overrides.model`, which is
 * false for the string `'default'`, and the learner was therefore never
 * consulted from the composer at all. Measured in production over 54 runs: 46
 * carried `source: 'explicit'` against 7 `learned`, and every one of the 42
 * runs submitted as "Auto" was served by `claude-opus-5` — the CLI's own
 * default, which is the most expensive tier. Choosing Auto did the opposite of
 * what it said: it switched the learner off *and* pinned the flagship.
 *
 * Anything deciding "did the operator pin a model?" must ask `isAutoModel`,
 * never truthiness.
 */
export const AUTO_MODEL = 'default';
/** Did the operator leave the model to the learner rather than pin one? */
export function isAutoModel(model) {
    return model === undefined || model === null || model === AUTO_MODEL;
}
/**
 * Did the operator leave the effort to the model rather than pin one?
 *
 * `null` is the composer's "Auto" for effort — a deferral, not a value. It
 * reads as absence everywhere else, which is why the model half of this pair
 * is the one that broke.
 */
export function isAutoEffort(effort) {
    return effort === undefined || effort === null;
}
export const ThinkingMode = z.enum(['adaptive', 'enabled', 'disabled']);
/**
 * Permission modes, mirroring the Agent SDK's `PermissionMode` exactly.
 *
 * - `default`           — prompt the operator for anything dangerous.
 * - `plan`              — research and propose; no tool ever executes.
 * - `acceptEdits`       — auto-approve file edits, still prompt for the rest.
 * - `dontAsk`           — never prompt; deny anything not pre-approved.
 * - `auto`              — a model classifier decides, prompting only when unsure.
 * - `bypassPermissions` — no checks at all.
 *
 * `bypassPermissions` is intentionally *not* a default anywhere: it is only
 * reachable when the operator opts in per workspace, and the API refuses it
 * unless `METACLAUDE_ALLOW_BYPASS_PERMISSIONS` is set on the container.
 */
export const PermissionMode = z.enum([
    'default',
    'plan',
    'acceptEdits',
    'dontAsk',
    'auto',
    'bypassPermissions',
]);
/**
 * The modes a run may hold when nobody is watching, ordered by capability.
 *
 * Deliberately not the full `PermissionMode` set. `default` and `auto` can open
 * a permission prompt and nobody is there to answer one: the request would sit
 * for ten minutes and then fail, which is a worse answer than a refusal.
 * `bypassPermissions` is absent because an unattended caller is exactly the one
 * that must never have it.
 *
 * `plan` executes nothing at all, `dontAsk` runs what the workspace has already
 * allowed and refuses the rest, `acceptEdits` adds file edits. A run takes the
 * *lesser* of this and the workspace's own mode — `capPermissionMode`.
 *
 * One definition, two readers: a token's ceiling (`ApiTokenCeiling`) and the
 * ceiling a run was admitted under (`Run.ceiling`). They were the same three
 * values written twice, and the second copy is what would have drifted.
 */
export const UnattendedCeiling = z.enum(['plan', 'dontAsk', 'acceptEdits']);
/** Operator-facing copy for each mode, surfaced in the UI's mode picker. */
export const PERMISSION_MODE_INFO = {
    plan: {
        label: 'Plan',
        description: 'Research and propose only. No tool is ever executed.',
        risk: 'low',
    },
    default: {
        label: 'Ask',
        description: 'Ask before anything that writes, deletes or runs a command.',
        risk: 'low',
    },
    acceptEdits: {
        label: 'Accept edits',
        description: 'File edits apply automatically; commands still need approval.',
        risk: 'medium',
    },
    auto: {
        label: 'Auto',
        description: 'A classifier decides, and asks you only when it is unsure.',
        risk: 'medium',
    },
    dontAsk: {
        label: "Don't ask",
        description: 'Never prompt. Anything not pre-approved is denied outright.',
        risk: 'medium',
    },
    bypassPermissions: {
        label: 'Bypass',
        description: 'No permission checks at all. Only for disposable sandboxes.',
        risk: 'high',
    },
};
/* -------------------------------------------------------------------------- */
/* Users & auth                                                                */
/* -------------------------------------------------------------------------- */
export const UserRole = z.enum(['owner', 'operator', 'viewer']);
export const User = z.object({
    id: z.string(),
    username: z.string().min(3).max(64),
    displayName: z.string().max(120),
    role: UserRole,
    totpEnabled: z.boolean(),
    createdAt: Millis,
    lastLoginAt: Millis.nullable(),
});
export const LoginResponse = z.discriminatedUnion('status', [
    z.object({ status: z.literal('ok'), user: User, csrfToken: z.string() }),
    z.object({ status: z.literal('totp_required') }),
]);
/* -------------------------------------------------------------------------- */
/* Workspaces                                                                  */
/* -------------------------------------------------------------------------- */
/**
 * A workspace is the unit of isolation: a directory on disk, plus the default
 * agent policy applied to sessions created inside it.
 */
export const WorkspaceSettings = z.object({
    defaultModel: ModelSelector.default('default'),
    defaultEffort: EffortLevel.nullable().default(null),
    defaultPermissionMode: PermissionMode.default('default'),
    thinking: ThinkingMode.default('adaptive'),
    thinkingBudgetTokens: z.number().int().min(1024).max(200_000).nullable().default(null),
    maxTurns: z.number().int().min(1).max(1000).nullable().default(null),
    maxBudgetUsd: z.number().min(0).max(1000).nullable().default(null),
    allowedTools: z.array(z.string()).default([]),
    disallowedTools: z.array(z.string()).default([]),
    additionalDirectories: z.array(z.string()).default([]),
    systemPromptAppend: z.string().max(20_000).default(''),
    /**
     * The language the agent answers in.
     *
     * `auto` is the default and changes nothing: the model follows the language
     * it is written to, which is right most of the time. It is *sub*agents that
     * drift — the 23 in the library carry English prompts and English
     * descriptions, so work delegated out of a French conversation comes back in
     * English, and nothing in the run stack had an opinion about it. One line in
     * the system prompt settles it for the whole run, delegations included,
     * which is cheaper and more reliable than translating a catalogue.
     */
    language: z.enum(['auto', 'fr', 'en']).default('auto'),
    /** Inject retrieved long-term memory into the system prompt. */
    memoryEnabled: z.boolean().default(true),
    /**
     * Whether runs in this workspace retrieve from the knowledge library —
     * the operator's reference documents, workspace shelf plus the global one.
     * Distinct from memoryEnabled on purpose: what the system learned and what
     * it was handed to read are different trusts, switched separately.
     */
    knowledgeEnabled: z.boolean().default(true),
    /** Let the learning subsystem pick model/effort from past performance. */
    autoPolicyEnabled: z.boolean().default(true),
    /** Run the post-hoc reflexion pass that distils lessons from each run. */
    reflexionEnabled: z.boolean().default(true),
    /** Enable file checkpointing so runs can be rewound. */
    checkpointing: z.boolean().default(true),
    /**
     * Mirror this workspace's sessions to claude.ai as view-only. Off by
     * default: it publishes transcripts to the account, and it only has an
     * effect when the CLI's own account sign-in is the live credential — a
     * token is inference-only and cannot upload sessions.
     */
    mirrorSessions: z.boolean().default(false),
    /**
     * The board autopilot: when a card run ends, start the next To do card by
     * itself — one card at a time, success landing in Review, the quota guard
     * consulted before every automatic start. Off by default: draining a
     * backlog unattended is a decision, not a discovery.
     */
    autoWorkBoard: z.boolean().default(false),
    /**
     * Marketplace plugins enabled here, keyed `plugin@marketplace` — the CLI's
     * own `enabledPlugins` format. A key without its marketplace half would be
     * meaningless to the CLI, so the shape refuses it at the edge.
     */
    enabledPlugins: z.record(z.string().regex(/^[^@\s]+@[^@\s]+$/), z.boolean()).default({}),
    /**
     * Let the advisor analyse this workspace by itself, at most once a day:
     * a run that reads the state of things and proposes tickets, automations,
     * skills, agents and vetted MCP servers. Off by default — an agent that
     * studies your workspace unprompted is a decision, not a discovery. The
     * manual "Ask the advisor" button works either way.
     */
    advisorAuto: z.boolean().default(false),
    /**
     * Let Metaclaude review this workspace's own instructions, at most once a
     * week: a pass that reads what the last window of runs actually did and
     * proposes rewrites of the workspace's standing instructions, its skills,
     * its subagents and its automations' prompts. Off by default, and for a
     * stronger reason than the advisor's — the advisor proposes things that do
     * not exist yet and land disabled, while a revision rewrites a text that is
     * in force on the very next run. Every proposal still waits for a person;
     * this switch decides only whether the question gets asked unprompted.
     *
     * The default carries the decision for every row written before the field
     * existed, the way `delegable` does: settings are reparsed through this
     * schema on every read, so `false` here is what a live deployment gets, and
     * an operator who wants it turns it on.
     */
    improvementAuto: z.boolean().default(false),
    /**
     * Whether another workspace's agent may consult this one by delegation.
     *
     * An opt-*out*, and the default carries the whole decision: settings are
     * reparsed through this schema on every read, so every row written before
     * this field existed gets what the default says. `false` would have made a
     * live deployment's workspaces silently unreachable at the next boot.
     *
     * It governs the peer directory and the `delegate` tool — not the steward,
     * which reaches every workspace through `runAsk`/`runStart` whatever this
     * says. Locking the operator's own agent out of a workspace is not what an
     * operator asks for by declining to be consulted by *other projects*.
     */
    delegable: z.boolean().default(true),
});
export const Workspace = z.object({
    id: z.string(),
    name: z.string().min(1).max(120),
    slug: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9][a-z0-9-]*$/),
    description: z.string().max(2000),
    /** Absolute path inside the container. Always under the workspaces root. */
    path: z.string(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    icon: z.string().max(48),
    archived: z.boolean(),
    settings: WorkspaceSettings,
    createdAt: Millis,
    updatedAt: Millis,
});
/* -------------------------------------------------------------------------- */
/* Sessions & runs                                                             */
/* -------------------------------------------------------------------------- */
export const SessionStatus = z.enum(['idle', 'running', 'waiting_approval', 'error', 'archived']);
export const Session = z.object({
    id: z.string(),
    workspaceId: z.string(),
    title: z.string().max(200),
    /** The Claude CLI session id, once the first run has initialised. */
    claudeSessionId: z.string().nullable(),
    status: SessionStatus,
    /**
     * The session's own choices, or what it inherits.
     *
     * One rule for the three: the workspace gives the base value, a session
     * follows it until somebody picks one here, and a session that picked keeps
     * its pick whatever the workspace later says. Inherited is Auto for the
     * model, `null` for the effort and `null` for the mode — the mode's own
     * `'default'` is a real mode, Ask, never "unset". The row used to hold a
     * *copy* of the workspace's values, and a model set on the workspace then
     * reached no existing session.
     */
    model: ModelSelector,
    effort: EffortLevel.nullable(),
    permissionMode: PermissionMode.nullable(),
    agentName: z.string().nullable(),
    pinned: z.boolean(),
    archived: z.boolean(),
    totalCostUsd: z.number().nonnegative(),
    totalInputTokens: z.number().int().nonnegative(),
    totalOutputTokens: z.number().int().nonnegative(),
    /**
     * What the session read back from, and wrote into, the prompt cache.
     *
     * These are not a footnote to the two above — they are the bulk of it. In an
     * agentic loop every turn resends the whole conversation, so a session's real
     * consumption lives here: measured over 54 runs, 12.04M read and 1.41M
     * written against 650k of genuine input. Defaulted so a row written before
     * the columns existed still parses.
     */
    totalCacheReadTokens: z.number().int().nonnegative().default(0),
    totalCacheCreationTokens: z.number().int().nonnegative().default(0),
    runCount: z.number().int().nonnegative(),
    createdAt: Millis,
    updatedAt: Millis,
    lastActivityAt: Millis,
    /**
     * When the operator last had this session open. Older than `lastActivityAt`
     * means something arrived that nobody has read — what the sidebar's dot and
     * the workspace card's dot are made of. Stamped by opening the session, not
     * by anything the agent does.
     */
    lastReadAt: Millis,
});
/** Is there something in this session the operator has not seen? */
export function isSessionUnread(session) {
    return session.lastActivityAt > session.lastReadAt;
}
export const RunStatus = z.enum([
    'queued',
    'running',
    'waiting_approval',
    'succeeded',
    'failed',
    'interrupted',
]);
/**
 * The coarse task categories the classifier assigns to a run, and the values
 * `Run.category` can hold.
 *
 * Here rather than beside the classifier because both sides read them: the API
 * writes the column, and the web app has to *name* them — an event trigger's
 * filter is matched against this string, so a form that cannot list them asks
 * the operator to guess an English identifier from a French screen. They are
 * identifiers, never translated.
 */
export const TASK_CATEGORIES = [
    'code_write',
    'code_edit',
    'debug',
    'review',
    'test',
    'refactor',
    'research',
    'explain',
    'plan',
    'ops',
    'data',
    'write',
    'chat',
];
/**
 * The composer's Tools picker, as a contract. A name may not be both cut and
 * preferred — the refinement rejects the contradiction where it is typed
 * rather than letting the run resolve it arbitrarily.
 */
export const ToolControls = z
    .object({
    requiredSkills: z.array(z.string().min(1).max(128)).max(16).default([]),
    excludedMcpServers: z.array(z.string().min(1).max(128)).max(32).default([]),
    preferredMcpServers: z.array(z.string().min(1).max(128)).max(32).default([]),
})
    .refine((controls) => !controls.excludedMcpServers.some((name) => controls.preferredMcpServers.includes(name)), { message: 'An MCP server cannot be both excluded and preferred.' });
export const RunPolicy = z.object({
    model: ModelSelector,
    effort: EffortLevel.nullable(),
    permissionMode: PermissionMode,
    thinking: ThinkingMode,
    thinkingBudgetTokens: z.number().int().nullable(),
    agentName: z.string().nullable(),
    /**
     * Standing multi-agent orchestration for this run (the CLI's "ultracode"):
     * xhigh effort plus fan-out workflows by default. Costs tokens accordingly,
     * and needs an xhigh-capable model — the CLI falls back gracefully on one
     * that is not. Defaulted so every policy stored before the field existed
     * still parses.
     */
    ultracode: z.boolean().default(false),
    /**
     * Per-message tool steering, from the composer's Tools picker. Three honest
     * levers, none of which widens a permission: required skills narrow what
     * the CLI loads to exactly those (its `skills` option is a context filter,
     * not a sandbox — the permission gates still apply); an excluded MCP server
     * is simply not mounted for this run; a preferred one stays mounted and the
     * preference is written into the system prompt, because an availability
     * filter can force absence but only words can ask for use. Absent means
     * the agent's own judgement over everything the workspace offers.
     */
    toolControls: ToolControls.optional(),
    /** Where this policy came from — user choice, workspace default, or the bandit. */
    source: z.enum(['explicit', 'workspace', 'learned']),
});
export const RunUsage = z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    cacheCreationTokens: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    durationMs: z.number().int().nonnegative(),
    turns: z.number().int().nonnegative(),
});
export const Run = z.object({
    id: z.string(),
    sessionId: z.string(),
    workspaceId: z.string(),
    prompt: z.string(),
    status: RunStatus,
    policy: RunPolicy,
    usage: RunUsage,
    /** Task category assigned by the classifier, used for policy learning. */
    category: z.string().nullable(),
    error: z.string().nullable(),
    /** Explicit user feedback, -1 (bad) .. +1 (good). Null until rated. */
    rating: z.number().min(-1).max(1).nullable(),
    /** Composite reward computed by the learning subsystem, 0..1. */
    reward: z.number().min(0).max(1).nullable(),
    /** Whether this run was started by an automation rather than a human. */
    /**
     * `delegation` marks a run another workspace's agent asked for; `api` marks
     * one an outside application asked for through the MCP gateway.
     *
     * `api` is not cosmetic. The run history is the only place an operator sees
     * what the agent actually did, and a run started by a token nobody was
     * watching must not read there as a run somebody typed.
     */
    triggeredBy: z.enum(['user', 'automation', 'loop', 'system', 'delegation', 'api']),
    /**
     * The ceiling this run was admitted under, or null when nothing bounded it.
     *
     * Set for a run an outside token asked for, and carried onto every run *that*
     * run causes — a delegation to another workspace takes the target's own mode
     * capped by this. Without it the ceiling would bound the first hop only, and
     * a token limited to `dontAsk` could reach a workspace set to `acceptEdits`
     * by asking its agent to consult one, which is the promise "the most a run it
     * starts may do" read as applying to the first run alone.
     *
     * Frozen at admission like the run's own permission mode, and for the same
     * reason: a run already in flight is not re-judged when the token changes.
     */
    ceiling: UnattendedCeiling.nullable(),
    /**
     * The CLI's uuid for the user message that started this run.
     *
     * The anchor a rewind restores to: the uuid Metaclaude puts on the message
     * it hands the CLI, so it exists the moment the run starts. Null when the
     * run cannot be rewound — checkpointing was off for the workspace, or the
     * run predates the feature. The UI treats both the same way: no rewind
     * offered.
     *
     * It used to be read off a replay acknowledgement the CLI was expected to
     * send back, and Claude Code 2.1.218 sends no user message at all in
     * streaming-input mode: the field was null for every run ever recorded.
     */
    rewindPoint: z.string().nullable(),
    /**
     * The model that actually served the run, from the CLI's own init message.
     * The policy records what was *asked for* — under Auto that can be
     * literally 'default', with the CLI choosing. Null when the CLI never said.
     */
    servedModel: z.string().nullable().default(null),
    startedAt: Millis,
    finishedAt: Millis.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Transcript events                                                           */
/* -------------------------------------------------------------------------- */
export const ToolCallStatus = z.enum(['pending', 'approved', 'denied', 'running', 'ok', 'error']);
/**
 * A transcript event is the atomic, persisted unit of a conversation. The web
 * app renders a run purely from its ordered event list, which means reload,
 * replay and live streaming all share one code path.
 */
export const TranscriptEvent = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('user_message'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        text: z.string(),
        // `attachmentId` and `mime` are optional because events persisted before
        // attachments shipped carry neither; the renderer degrades to a plain chip.
        attachments: z
            .array(z.object({
            name: z.string(),
            path: z.string(),
            bytes: z.number(),
            attachmentId: z.string().optional(),
            mime: z.string().optional(),
        }))
            .default([]),
    }),
    z.object({
        kind: z.literal('assistant_text'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        text: z.string(),
        /** Set while the model is still streaming this block. */
        streaming: z.boolean().default(false),
    }),
    z.object({
        kind: z.literal('thinking'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        text: z.string(),
        streaming: z.boolean().default(false),
    }),
    z.object({
        kind: z.literal('tool_call'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        toolUseId: z.string(),
        name: z.string(),
        input: z.unknown(),
        status: ToolCallStatus,
        /** Truncated, display-ready result. Full payload lives in `resultPath`. */
        result: z.string().nullable().default(null),
        resultIsError: z.boolean().default(false),
        durationMs: z.number().int().nullable().default(null),
    }),
    z.object({
        kind: z.literal('subagent'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        agentName: z.string(),
        description: z.string(),
        status: z.enum(['running', 'ok', 'error']),
        summary: z.string().nullable().default(null),
    }),
    z.object({
        kind: z.literal('todo'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        items: z.array(z.object({
            content: z.string(),
            status: z.enum(['pending', 'in_progress', 'completed']),
            activeForm: z.string().optional(),
        })),
    }),
    z.object({
        kind: z.literal('diff'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        path: z.string(),
        additions: z.number().int(),
        deletions: z.number().int(),
        patch: z.string(),
    }),
    z.object({
        kind: z.literal('system'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        level: z.enum(['info', 'warn', 'error']),
        message: z.string(),
        data: z.unknown().optional(),
    }),
    z.object({
        kind: z.literal('result'),
        id: z.string(),
        runId: z.string(),
        seq: z.number().int(),
        at: Millis,
        status: RunStatus,
        usage: RunUsage,
        error: z.string().nullable().default(null),
    }),
]);
/* -------------------------------------------------------------------------- */
/* Tool approvals                                                              */
/* -------------------------------------------------------------------------- */
export const ApprovalRequest = z.object({
    id: z.string(),
    runId: z.string(),
    sessionId: z.string(),
    workspaceId: z.string(),
    toolUseId: z.string(),
    toolName: z.string(),
    input: z.unknown(),
    /** Human-readable one-liner, e.g. `Bash: rm -rf build/`. */
    summary: z.string(),
    /** Heuristic risk assessment used to colour the UI and pick defaults. */
    risk: z.enum(['low', 'medium', 'high']),
    reason: z.string().nullable(),
    createdAt: Millis,
    expiresAt: Millis,
});
export const ApprovalDecision = z.object({
    approvalId: z.string(),
    approved: z.boolean(),
    /** Remember this decision for the rest of the session. */
    remember: z.boolean().default(false),
    reason: z.string().max(500).optional(),
});
/* -------------------------------------------------------------------------- */
/* Memory & learning                                                           */
/* -------------------------------------------------------------------------- */
/**
 * Three memory types, following the standard cognitive-architecture split:
 *
 * - `episodic`   — what happened during a specific run.
 * - `semantic`   — durable facts about the user, the projects, the conventions.
 * - `procedural` — how to do something; the seed material for skills.
 */
/**
 * The one place a memory tag's shape is decided, shared because two sides
 * decide it.
 *
 * Three writers reach memory — the web form, the reflexion pass, and the edit
 * route — and none agreed with the others: the form lowercased what it
 * parsed, reflexion handed over whatever case the model produced. `new Set`
 * over strings is case-sensitive, so merging a repeated observation kept
 * `Bail` *and* `bail`, and every repeat added another variant until the
 * 24-tag cap began evicting real ones (measured: 20 distinct tags filling all
 * 24 slots with case pairs).
 *
 * It lives here rather than in the API because the web needs the same rule to
 * show the user what will actually be stored. Folding happens before the cap,
 * so the budget is never spent on variants of one word.
 */
export function normaliseTags(tags) {
    return [...new Set(tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean))].slice(0, 24);
}
export const MemoryKind = z.enum(['episodic', 'semantic', 'procedural']);
/**
 * How long a memory is meant to hold, orthogonal to its kind.
 *
 * `standing` — a convention or preference of the operator: injected into every
 * run of its scope, never decayed, never retrieved by similarity because it
 * applies whatever the request is about. `durable` — the default: retrieved by
 * relevance, reinforced on use, forgotten on the long half-life. `volatile` — a
 * fact that can stop being true: retrieved the same way, forgotten three times
 * faster, and the one shelf a machine write may replace.
 */
export const MemoryShelf = z.enum(['standing', 'durable', 'volatile']);
/**
 * What the reflexion pass proposed after a run and what the memory gate made
 * of each proposal — carried in the run's insight so the operator can see
 * what was refused, and keep any of it in one gesture. Declared here rather
 * than in api-contracts because the Memory page parses it at runtime.
 */
export const GateLevel = z.enum(['preference', 'lesson', 'fact', 'state', 'redundant', 'episodic', 'unjudged']);
/**
 * What became of one note the gate judged.
 *
 * `forgotten` is not a verdict the gate can reach: it is what a *kept* note
 * becomes when the memory it created stops existing — the operator deleted it,
 * or decay reaped it. Without it, such a note kept claiming to be `kept` while
 * pointing at a memory that was gone, so the row offered "Forget" forever and
 * pressing it did nothing visible. Naming the state is what lets the decision
 * be reversible in both directions, which is the whole point of that row.
 */
export const GateOutcome = z.enum([
    'kept',
    'superseded',
    'skipped',
    'over-budget',
    'unjudged',
    'forgotten',
]);
export const ReflexionInsightPayload = z.object({
    kind: z.literal('reflexion'),
    decisions: z.array(z.object({
        title: z.string(),
        content: z.string(),
        kind: MemoryKind,
        tags: z.array(z.string()),
        level: GateLevel,
        outcome: GateOutcome,
        reason: z.string(),
        /** Set once a memory exists for this decision — by the gate, or by the operator keeping it later. */
        memoryId: z.string().nullable(),
        shelf: MemoryShelf.nullable(),
    })),
});
export const Memory = z.object({
    id: z.string(),
    workspaceId: z.string().nullable(),
    kind: MemoryKind,
    shelf: MemoryShelf.default('durable'),
    /** Set when the memory was retired — a soft delete, restorable for thirty days. */
    retiredAt: Millis.nullable().default(null),
    /** The memory that replaced this one, when retirement was a supersession. */
    supersededBy: z.string().nullable().default(null),
    /** Short retrieval key, ~1 sentence. */
    title: z.string().max(300),
    /** Full body injected into context when retrieved. */
    content: z.string(),
    tags: z.array(z.string()).default([]),
    /** 0..1 — how much we trust this memory. Reinforced on use, decayed by time. */
    confidence: z.number().min(0).max(1),
    /** Number of times this memory was retrieved into a run. */
    useCount: z.number().int().nonnegative(),
    /** Times a run that used this memory succeeded. Drives reinforcement. */
    successCount: z.number().int().nonnegative(),
    /** Pinned memories are never decayed or garbage-collected. */
    pinned: z.boolean(),
    sourceRunId: z.string().nullable(),
    createdAt: Millis,
    updatedAt: Millis,
    lastUsedAt: Millis.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Skills, agents, MCP                                                         */
/* -------------------------------------------------------------------------- */
export const McpTransport = z.enum(['stdio', 'sse', 'http']);
/* -------------------------------------------------------------------------- */
/* Automations (the "loop" engine)                                             */
/* -------------------------------------------------------------------------- */
export const AutomationTrigger = z.discriminatedUnion('type', [
    z.object({ type: z.literal('cron'), expression: z.string().min(1).max(200) }),
    z.object({ type: z.literal('interval'), everyMs: z.number().int().min(60_000) }),
    z.object({ type: z.literal('manual') }),
    /**
     * Fired by the outcome of another run in the same workspace.
     *
     * `run_failed` and `run_succeeded` have an emitter; `session_idle` and
     * `file_changed` are named here since the first schema and have none, so the
     * scheduler refuses them at creation rather than accept a trigger that never
     * fires. `filter` is a word that must appear in the run's category or prompt.
     *
     * `automations` names which automations of the same workspace this one
     * watches, and the two modes are exclusive: absent, it watches the runs a
     * person, a token or a delegation started — never one an automation
     * produced, which is what the field replaces as a loop guard; present, it
     * watches the end of exactly those automations and nothing else. The guard
     * moves from "no automation may ever be heard" to "only the one that named
     * it", so what forbids the loop is now the acyclicity of the declared graph,
     * refused at create and at update.
     *
     * An empty array is not a mode. It is what pruning leaves behind when the
     * last source is deleted — a watcher that hears nothing, and says so —
     * because falling back to the human population would silently repurpose an
     * automation the operator wrote for a chain. The scheduler refuses an empty
     * list at the edge; only pruning may produce one.
     */
    z.object({
        type: z.literal('event'),
        event: z.enum(['run_failed', 'run_succeeded', 'session_idle', 'file_changed']),
        filter: z.string().max(300).optional(),
        automations: z.array(z.string().min(1).max(64)).max(32).optional(),
    }),
]);
/** The event triggers something actually emits. The other two are declared and refused. */
export const EMITTED_AUTOMATION_EVENTS = ['run_failed', 'run_succeeded'];
/** The automations a trigger declares as its sources — empty for every other kind. */
export function declaredSources(trigger) {
    return trigger.type === 'event' ? (trigger.automations ?? []) : [];
}
/**
 * Whether this trigger watches automations rather than the runs people start.
 *
 * True for an **empty** list, which is the whole reason this is a function: an
 * empty list is a watcher whose last source was deleted — it watches nothing
 * and is waiting to be repaired — where an absent one watches everything a
 * person, a token or a delegation runs. Seven call sites ask this, and written
 * inline one of them eventually becomes `automations?.length > 0`: the same
 * expression for six of them, and a silent change of population for the
 * seventh.
 */
export function watchesAutomations(trigger) {
    return trigger.type === 'event' && trigger.automations !== undefined;
}
/**
 * Follow declared sources from `from` and return the first path that reaches
 * `target`, or null when none does.
 *
 * Shared because both sides ask it and must not answer differently: the
 * scheduler refuses a trigger that would close a loop, and the editor declines
 * to *offer* the tick that would. Two walks would drift the first time either
 * was touched, and the one that drifts is the form — which then offers a tick
 * whose only outcome is a refusal, or hides one that was legal.
 *
 * The path is returned rather than a boolean because the refusal has to name
 * the loop it found: "Deploy → Report → Deploy" is actionable where "that
 * would loop" is not. `seen` is what keeps it terminating over a graph that
 * already holds a cycle — impossible to store, but the form runs this over
 * whatever the server currently says.
 */
export function watchPath(sourcesOf, from, target) {
    const seen = new Set();
    const stack = from.map((id) => [id]);
    while (stack.length > 0) {
        const path = stack.pop();
        const node = path[path.length - 1];
        if (node === target)
            return path;
        if (seen.has(node))
            continue;
        seen.add(node);
        for (const next of sourcesOf(node))
            stack.push([...path, next]);
    }
    return null;
}
/**
 * How a firing runs, and what it does when it ends.
 *
 * Exported rather than declared inline inside `Automation`, and the API
 * validates *this* object: the route used to carry a hand-written copy of the
 * same five fields, the copy never gained `notify`, and Zod strips what it
 * does not know — so the checkbox was saved by the browser, dropped at the
 * edge without a word, and the automation stayed silent while the form said
 * otherwise. One definition is the fix; a second copy is the bug.
 */
export const AutomationPolicy = z.object({
    model: ModelSelector.default('default'),
    effort: EffortLevel.nullable().default(null),
    permissionMode: PermissionMode.default('default'),
    // Bounded here rather than at the edge: the bound was on the route's copy,
    // and a bound that lives on a copy is lost the moment the copy is.
    agentName: z.string().max(64).nullable().default(null),
    maxTurns: z.number().int().min(1).max(500).nullable().default(null),
    /**
     * Push the operator when a firing ends. Off by default: the machinery
     * works while they sleep, and a channel that wakes them for it gets
     * disabled within a week — but a brief nobody hears about is a brief
     * read ten hours late, so the automations whose whole point is to be
     * read opt in.
     */
    notify: z.boolean().default(false),
});
export const Automation = z.object({
    id: z.string(),
    workspaceId: z.string(),
    name: z.string().min(1).max(120),
    description: z.string().max(2000),
    prompt: z.string().min(1).max(100_000),
    trigger: AutomationTrigger,
    policy: AutomationPolicy.default({
        model: 'default',
        effort: null,
        permissionMode: 'default',
        agentName: null,
        maxTurns: null,
        notify: false,
    }),
    /**
     * When set, each firing continues the same Claude session instead of starting
     * fresh — this is what turns an automation into a long-running agentic loop
     * that accumulates context across firings.
     */
    continuous: z.boolean().default(false),
    sessionId: z.string().nullable(),
    /**
     * Which copies of this automation are the same automation.
     *
     * An automation reaches one workspace by schema, so serving two projects
     * means two rows — and two rows drift the moment one is edited. The family is
     * what makes that drift a decision rather than an accident: it is set when
     * one is duplicated from another, shared by every copy including the
     * original, and it answers the only question the editor needs — who else
     * should hear about this change?
     *
     * Nothing propagates on its own. Null for an automation that was never
     * duplicated, which is a different state from being alone in a family.
     */
    familyId: z.string().nullable().default(null),
    /** Stop the loop after this many consecutive failures. 0 disables the guard. */
    maxConsecutiveFailures: z.number().int().min(0).max(100).default(3),
    consecutiveFailures: z.number().int().nonnegative().default(0),
    enabled: z.boolean(),
    lastRunAt: Millis.nullable(),
    lastStatus: RunStatus.nullable(),
    nextRunAt: Millis.nullable(),
    runCount: z.number().int().nonnegative(),
    createdAt: Millis,
    updatedAt: Millis,
});
/* -------------------------------------------------------------------------- */
/* Files                                                                       */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* Attachments                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * What a message may carry, and how much.
 *
 * Files land under the workspace's `attachments/` directory — on disk, where
 * the agent's own Read tool handles images and PDFs natively and every other
 * type through its ordinary tooling. Small images and PDFs additionally ride
 * the prompt itself as content blocks (the API caps a block around 5 MB of
 * base64, hence the conservative inline ceilings); everything else reaches
 * the model as a named path, which is the robust channel for any size.
 */
export const ATTACHMENT_LIMITS = {
    maxBytes: 20 * 1024 * 1024,
    maxPerMessage: 8,
    inlineImageBytes: 2 * 1024 * 1024,
    inlinePdfBytes: 4 * 1024 * 1024,
};
/**
 * The types a message accepts. An allowlist rather than a denylist: the point
 * of an attachment is that the agent can do something with it, and honesty
 * about what is supported beats accepting bytes that will sit inert.
 */
export const ATTACHMENT_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'application/pdf',
    'text/plain',
    'text/markdown',
    'text/csv',
    'text/html',
    'application/json',
    'application/zip',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
/* -------------------------------------------------------------------------- */
/* The knowledge library's files                                               */
/* -------------------------------------------------------------------------- */
/**
 * What the knowledge library accepts as a file.
 *
 * A closed allowlist, and the same reasoning as the attachments above: a type
 * listed here is a type an extractor exists for. The difference is what
 * "supported" means — an attachment only has to reach the agent's tools,
 * while a document has to become *text* the retrieval index can hold, so
 * images and archives are absent rather than merely inert.
 *
 * `extract/index.ts` holds one extractor per entry and a test walks this list
 * to prove it: a type accepted at the edge and unroutable underneath would be
 * a 500 dressed as a feature.
 */
export const KNOWLEDGE_MIME_TYPES = [
    'text/plain',
    'text/markdown',
    'text/csv',
    'text/html',
    'application/json',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
/**
 * The upload ceiling, matching the attachments': 20 MB is what a browser can
 * base64 into a request this API already accepts. What bounds the *document*
 * is different and stricter — the extracted text still has to fit
 * `MAX_DOCUMENT_BYTES` — so a 20 MB spreadsheet is refused for its text, not
 * for its bytes, and the message says which.
 */
export const KNOWLEDGE_LIMITS = {
    maxBytes: 20 * 1024 * 1024,
};
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* Library categories — how skills and agents are shelved                      */
/* -------------------------------------------------------------------------- */
/**
 * Where a skill or agent belongs on the shelf.
 *
 * Two halves, in this order: the domains of work, then the domains of a life.
 * The second half exists because nothing in this system is specific to code —
 * the memory, the policy and the board serve a house move or a week of meals
 * exactly as they serve a refactor — and a library that only spoke of
 * engineering quietly said otherwise.
 *
 * `general` stays last on purpose: a taxonomy without an "everything else"
 * drawer forces bad filing.
 */
export const LibraryCategory = z.enum([
    'engineering',
    'writing',
    'data',
    'ops',
    'research',
    'product',
    'home',
    'health',
    'money',
    'learning',
    'travel',
    'career',
    'general',
]);
export const LIBRARY_CATEGORIES = LibraryCategory.options;
/* -------------------------------------------------------------------------- */
/* Google — the one connector Metaclaude authorises for itself                 */
/* -------------------------------------------------------------------------- */
/**
 * What the operator is granting, one checkbox each.
 *
 * Deliberately finer than "Gmail, Calendar, Drive": reading mail and sending
 * mail are different powers, and an agent that can read your inbox to prepare
 * a summary has no business also being able to send from it unless you said
 * so. Each entry maps to exactly one Google scope in
 * `apps/api/src/integrations/google/scopes.ts`, and the granted set decides
 * both the consent screen and which tools the MCP server registers — so a
 * capability you did not grant does not merely fail, it does not exist.
 */
export const GoogleGrant = z.enum([
    'gmail.read',
    'gmail.send',
    'calendar.read',
    'calendar.write',
    'drive.read',
    'drive.write',
]);
export const GOOGLE_GRANTS = GoogleGrant.options;
/* The board — tasks the operator and the agents share                         */
/* -------------------------------------------------------------------------- */
export const TaskStatus = z.enum(['backlog', 'todo', 'in_progress', 'review', 'done']);
export const TaskPriority = z.enum(['low', 'normal', 'high', 'urgent']);
/**
 * What a card *is*, which is not what it costs or when it is due.
 *
 * Three, because a fourth is a taxonomy nobody maintains: something is
 * broken (`bug`), something must be done (`task`), or something could be
 * better than it is (`improvement`). The distinction earns its place by
 * changing how a card is read — a bug in Review means "check the fix", an
 * improvement in Backlog is a candidate rather than a debt — and by letting a
 * board be scanned for one kind without reading every title.
 */
export const TaskKind = z.enum(['bug', 'task', 'improvement']);
/**
 * One card. A real schema, not a type: board updates ride the socket, so
 * `parseWireFrame` genuinely reaches this at runtime.
 *
 * `orderKey` is a fractional position within (workspace, status) — the server
 * assigns it on every move, so ordering survives concurrent edits without a
 * renumbering sweep. `archivedAt` is a timestamp rather than a status: an
 * archived card keeps the column it died in, which is what a restore should
 * restore. `assignee` names who works it — the operator or this workspace's
 * agent; the agent half becomes actionable in the delegation lot.
 */
export const BoardTask = z.object({
    id: z.string(),
    workspaceId: z.string(),
    /** Set when this card was decomposed out of a larger one. */
    parentId: z.string().nullable().default(null),
    title: z.string().min(1).max(300),
    description: z.string().max(20_000).default(''),
    status: TaskStatus,
    /** Defaulted: every card written before kinds existed is a task. */
    kind: TaskKind.default('task'),
    priority: TaskPriority.default('normal'),
    assignee: z.enum(['user', 'agent']).nullable().default(null),
    /** The run currently (or last) working this card. */
    runId: z.string().nullable().default(null),
    dueAt: Millis.nullable().default(null),
    orderKey: z.string(),
    /** Why the card cannot advance — human-readable, cleared on movement. */
    blockedReason: z.string().nullable().default(null),
    /** 'user:<name>' or 'agent:<runId>' — who created it is part of the record. */
    createdBy: z.string(),
    createdAt: Millis,
    updatedAt: Millis,
    archivedAt: Millis.nullable().default(null),
});
export const TaskComment = z.object({
    id: z.string(),
    taskId: z.string(),
    /** 'user:<name>' or 'agent:<runId>'. */
    author: z.string(),
    body: z.string().min(1).max(10_000),
    createdAt: Millis,
});
/* -------------------------------------------------------------------------- */
/* Analytics                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * A claude.ai account sign-in held by the CLI itself (`claude auth login`,
 * run in the container). Distinct from a paired token: the CLI maintains and
 * refreshes it on its own, and its scopes decide what the account can do —
 * `full` means it carries the session-sync scopes a setup token never has.
 */
export const ClaudeCliLoginInfo = z.object({
    full: z.boolean(),
    scopes: z.array(z.string()),
    subscriptionType: z.string().nullable(),
    /**
     * When the *access* token expires — hours away, and rotated by the CLI on
     * its own. Almost never the number anybody wants.
     */
    expiresAt: z.number().nullable(),
    /**
     * When the sign-in itself ends, and nothing can extend it.
     *
     * The refresh token's expiry, and the only date worth watching: measured on
     * a live deployment, two backups a day apart showed `expiresAt` move while
     * this stayed at exactly the same instant. It is fixed-term, not rolling, so
     * activity does not push it back — and when it passes, every run fails to
     * authenticate at once.
     *
     * Null when the store does not carry one, which means *unknown* rather than
     * expired: a setup token has no such field, and neither does an older CLI.
     */
    signInEndsAt: z.number().nullable().default(null),
});
/**
 * Which credential a guided flow is out to obtain.
 *
 * `token` is `claude setup-token`: inference scope, a year long, sealed in the
 * vault. `account` is `claude auth login`: the full sign-in scopes, installed
 * in the CLI's own store, and the only one Anthropic grants session sync to.
 * They are not interchangeable and finishing one as the other would install
 * the wrong credential in the wrong place — hence a kind rather than a flag.
 */
export const ClaudePairingKind = z.enum(['token', 'account']);
/* -------------------------------------------------------------------------- */
/* Analytics                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * One workspace's share of the usage.
 *
 * Analytics could already scope to one workspace, which answers "how much did
 * this one cost" and never "which one is eating the quota" — and on a
 * subscription with a weekly ceiling, that second question is the one that
 * matters. It needs every workspace at once, so it cannot be a filter.
 */
export const WorkspaceUsage = z.object({
    workspaceId: z.string(),
    name: z.string(),
    /** The workspace's own colour, so the ranking is readable at a glance. */
    color: z.string(),
    runs: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    /**
     * The cache halves, on the same footing as the summary's.
     *
     * Left out, this row counted about 6% of what a workspace actually moved
     * while the summary above it counted all of it — so the same screen answered
     * "how many tokens" two ways, and the ranking meant to say which workspace is
     * spending the ceiling was drawn from the smallest part of the bill.
     * Defaulted so a response serialised before the fields existed still parses.
     */
    cacheReadTokens: z.number().int().nonnegative().default(0),
    cacheCreationTokens: z.number().int().nonnegative().default(0),
    successRate: z.number().min(0).max(1),
});
/**
 * The aggregate view of a period.
 *
 * Lives here rather than in the analytics service because both sides read it:
 * the shape was written out twice — once as an interface in `services/`, once
 * inline in the web client's `request<…>` — and the copies had already started
 * to matter, since adding a field to one silently left the other describing a
 * response it no longer receives.
 */
export const AnalyticsSummary = z.object({
    totalRuns: z.number().int().nonnegative(),
    successRate: z.number().min(0).max(1),
    totalCostUsd: z.number().nonnegative(),
    totalInputTokens: z.number().int().nonnegative(),
    totalOutputTokens: z.number().int().nonnegative(),
    /**
     * The two halves of the prompt the input figure leaves out. A turn that
     * re-sends a context the cache still holds reads it at a tenth of the
     * price; one that arrives after the cache has expired writes it again at
     * a quarter more — and on a conversation of short questions that write is
     * most of the bill. Measured by the steward before these existed: "ça va ?"
     * cost twice a three-tool investigation, and nothing here could say why.
     */
    totalCacheReadTokens: z.number().int().nonnegative(),
    totalCacheCreationTokens: z.number().int().nonnegative(),
    medianDurationMs: z.number().nonnegative(),
    p95DurationMs: z.number().nonnegative(),
    averageReward: z.number().nullable(),
    byModel: z.array(z.object({
        model: z.string(),
        runs: z.number().int().nonnegative(),
        costUsd: z.number().nonnegative(),
        successRate: z.number().min(0).max(1),
    })),
    byCategory: z.array(z.object({
        category: z.string(),
        runs: z.number().int().nonnegative(),
        averageReward: z.number().nullable(),
    })),
    byWorkspace: z.array(WorkspaceUsage),
});
/* -------------------------------------------------------------------------- */
/* What Claude itself offers                                                   */
/* -------------------------------------------------------------------------- */
/**
 * The catalogue the CLI reports for a workspace.
 *
 * Metaclaude used to describe Claude's capabilities from a hard-coded list
 * written when the page was built — three model names and their prices. The CLI
 * knows the real answer, and it changes without a Metaclaude release: which
 * models the subscription grants, which of them take an effort level, which
 * slash commands and subagents exist here, and whether each MCP server actually
 * connected.
 *
 * Everything is optional-with-a-default rather than required, because an older
 * CLI answers some of these and not others, and one missing control method must
 * not cost the operator the whole catalogue.
 */
export const ClaudeModelInfo = z.object({
    value: z.string(),
    displayName: z.string(),
    description: z.string().default(''),
    /** Present when the alias resolves to a dated model id. */
    resolvedModel: z.string().nullable().default(null),
    supportsEffort: z.boolean().default(false),
    supportedEffortLevels: z.array(EffortLevel).default([]),
    supportsAdaptiveThinking: z.boolean().default(false),
});
export const ClaudeCommandInfo = z.object({
    name: z.string(),
    description: z.string().default(''),
    argumentHint: z.string().default(''),
    aliases: z.array(z.string()).default([]),
});
export const ClaudeAgentInfo = z.object({
    name: z.string(),
    description: z.string().default(''),
    model: z.string().nullable().default(null),
});
export const ClaudeMcpTool = z.object({
    name: z.string(),
    description: z.string().default(''),
    /** The server's own advertised safety hints. Displayed, never enforced. */
    readOnly: z.boolean().nullable().default(null),
    destructive: z.boolean().nullable().default(null),
});
export const ClaudeMcpServerStatus = z.object({
    name: z.string(),
    status: z.enum(['connected', 'failed', 'needs-auth', 'pending', 'disabled', 'unknown']),
    error: z.string().nullable().default(null),
    serverName: z.string().nullable().default(null),
    serverVersion: z.string().nullable().default(null),
    scope: z.string().nullable().default(null),
    tools: z.array(ClaudeMcpTool).default([]),
});
export const ClaudeAccountInfo = z.object({
    /** Never shown in full; the API returns only what the operator already knows. */
    email: z.string().nullable().default(null),
    organization: z.string().nullable().default(null),
    subscriptionType: z.string().nullable().default(null),
    apiProvider: z.string().nullable().default(null),
});
/**
 * The subscription's quota picture, as the CLI itself reports it.
 *
 * Every window — the five-hour session window, the weekly ones, and the
 * per-model buckets — is normalised into one flat list so the screen renders
 * them uniformly and a bucket the server adds tomorrow costs nothing here.
 * The behaviours block is the CLI's own attribution of what has been eating
 * the quota, from its local transcripts: approximate by its own admission
 * (other devices and claude.ai are invisible to it), which the UI must say.
 */
export const ClaudeUsageWindow = z.object({
    /** Stable identity: 'five_hour', 'seven_day', … or 'model:<display name>'. */
    key: z.string(),
    label: z.string(),
    /** Percentage of the window used, 0–100; null when the server did not say. */
    utilization: z.number().nullable(),
    resetsAt: Millis.nullable(),
});
export const ClaudeUsageShare = z.object({ name: z.string(), pct: z.number() });
export const ClaudeUsageBehaviorWindow = z.object({
    requestCount: z.number(),
    sessionCount: z.number(),
    /** Overlapping categories — not a partition, so shares do not sum to 100. */
    behaviors: z.array(z.object({ key: z.string(), pct: z.number(), count: z.number() })),
    agents: z.array(ClaudeUsageShare),
    skills: z.array(ClaudeUsageShare),
    plugins: z.array(ClaudeUsageShare),
    mcpServers: z.array(ClaudeUsageShare),
});
/**
 * The doctor — the system examining itself, read-only.
 *
 * Every check is named, has one of three statuses, and speaks in sentences.
 * The report's own status is the worst of its checks, so a screen (or an
 * agent) can act on one field and drill into the rest.
 */
export const DoctorCheck = z.object({
    name: z.string(),
    status: z.enum(['ok', 'warn', 'fail']),
    summary: z.string(),
    /** Supporting evidence — the broken entry id, the failing slots, the path. */
    detail: z.string().nullable().default(null),
});
export const DoctorReport = z.object({
    status: z.enum(['ok', 'warn', 'fail']),
    checks: z.array(DoctorCheck),
    version: z.string(),
    ranAt: Millis,
});
export const ClaudeUsage = z.object({
    /** 'pro', 'max', … — null for API-key or third-party-provider sessions. */
    subscriptionType: z.string().nullable(),
    windows: z.array(ClaudeUsageWindow),
    /** The overage-credits arrangement, when the plan has one. */
    extraUsage: z
        .object({
        isEnabled: z.boolean(),
        monthlyLimit: z.number().nullable(),
        usedCredits: z.number().nullable(),
        utilization: z.number().nullable(),
    })
        .nullable(),
    behaviors: z
        .object({ day: ClaudeUsageBehaviorWindow, week: ClaudeUsageBehaviorWindow })
        .nullable(),
    /** What could not be read, by name — same convention as the catalogue. */
    unavailable: z.array(z.string()),
    fetchedAt: Millis,
});
/* -------------------------------------------------------------------------- */
/* Agent Plugins 1.0.0                                                         */
/* -------------------------------------------------------------------------- */
/**
 * The vendor-neutral plugin format published on 2026-08-06 by Amazon, Cursor,
 * Microsoft, OpenAI and Vercel, with Google joining as a core maintainer.
 *
 * One directory packages Agent Skills and MCP servers so the same folder works
 * across every client that implements the spec. The schemas below are a
 * transcription of 1.0.0, not an interpretation of it: where the spec says a
 * field is the only permitted set, this refuses the rest, because a manifest
 * that validates here and nowhere else would defeat the point of a standard.
 *
 * https://github.com/agentplugins/agent-plugins-spec
 */
export const PLUGIN_SCHEMA_URL = 'https://agent-plugins.org/schemas/1.0.0/plugin.schema.json';
export const PLUGIN_MCP_SCHEMA_URL = 'https://agent-plugins.org/schemas/1.0.0/mcp.schema.json';
export const PluginAuthor = z.object({
    name: z.string().optional(),
    email: z.string().optional(),
    url: z.string().optional(),
});
export const PluginManifest = z
    .object({
    $schema: z.string(),
    /** Lowercase alphanumeric, hyphens and periods; 1–64 characters. */
    name: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9][a-z0-9.-]*$/, 'A plugin name is lowercase letters, digits, hyphens and periods.'),
    version: z.string().optional(),
    description: z.string().optional(),
    author: PluginAuthor.optional(),
    homepage: z.string().optional(),
    repository: z.string().optional(),
    license: z.string().optional(),
    keywords: z.array(z.string()).optional(),
    /**
     * Client-specific data, keyed by reverse-domain namespace. The spec says a
     * client MUST ignore members it does not implement *without validating
     * them*, so this stays deliberately unshaped.
     */
    extensions: z.record(z.string(), z.unknown()).optional(),
})
    // "The only permitted top-level fields are …" — an unknown key is reported
    // rather than accepted, but per the spec it is not fatal, so the loader
    // strips and warns instead of refusing the plugin.
    .strict();
const PluginMcpStdio = z.object({
    type: z.literal('stdio'),
    /** A bare executable name, or a ./ path relative to the plugin root. */
    command: z.string().min(1),
    args: z.array(z.string()).optional(),
    env: z.record(z.string(), z.string()).optional(),
    cwd: z.string().optional(),
});
const PluginMcpHttp = z.object({
    type: z.literal('streamable-http'),
    url: z.string().url(),
    headers: z.record(z.string(), z.string()).optional(),
});
/** Legacy in 1.0.0, and still permitted. */
const PluginMcpSse = z.object({
    type: z.literal('sse'),
    url: z.string().url(),
    headers: z.record(z.string(), z.string()).optional(),
});
export const PluginMcpServer = z.discriminatedUnion('type', [
    PluginMcpStdio,
    PluginMcpHttp,
    PluginMcpSse,
]);
/** One skill found under `skills/`, as loaded from disk. */
export const PluginSkill = z.object({
    name: z.string(),
    description: z.string(),
    /** Absolute path to the skill's SKILL.md. */
    path: z.string(),
});
export const InstallPluginRequest = z
    .object({
    /** A git URL to clone, or an absolute path already on the server. */
    source: z.string().min(1).max(500),
})
    .strict();
/**
 * A plugin marketplace — a source the Claude CLI itself fetches plugins from.
 *
 * The shape mirrors the CLI's own `extraKnownMarketplaces` settings key, kept
 * to the two source kinds an operator can point at from a browser: a GitHub
 * repository or a direct `marketplace.json` URL. Adding one is owner-level:
 * a marketplace supplies skills, hooks and MCP servers the agent will run.
 */
export const MarketplaceSource = z.discriminatedUnion('source', [
    z
        .object({
        source: z.literal('github'),
        /**
         * `owner/repo`, one repository exactly. The owner-wildcard form
         * (`owner/*`) is meaningful only in managed policy lists; everywhere
         * else the CLI takes the `*` literally and the clone fails, so it is
         * refused here rather than stored broken.
         */
        repo: z.string().regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/),
        /** Branch or tag; the repository default when absent. */
        ref: z.string().min(1).max(120).optional(),
        /** Path to marketplace.json when it is not at the CLI's default. */
        path: z.string().min(1).max(300).optional(),
    })
        .strict(),
    z
        .object({
        source: z.literal('url'),
        /** https only: this file names code the agent will execute. */
        url: z.string().url().startsWith('https://').max(500),
    })
        .strict(),
]);
export const MarketplaceInput = z
    .object({
    /**
     * The marketplace id: the `extraKnownMarketplaces` key, and the suffix of
     * every `plugin@marketplace` entry — so no spaces and no `@`.
     */
    name: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/),
    source: MarketplaceSource,
})
    .strict();
export const Marketplace = MarketplaceInput.extend({
    id: z.string(),
    enabled: z.boolean(),
    createdAt: Millis,
});
/**
 * One plugin as a marketplace's own `marketplace.json` describes it. Parsed
 * leniently: the catalogue exists to be browsed, and a marketplace that adds
 * fields tomorrow must not stop listing today.
 */
export const MarketplacePlugin = z.object({
    name: z.string(),
    description: z.string().nullable().default(null),
    version: z.string().nullable().default(null),
    author: z.string().nullable().default(null),
});
//# sourceMappingURL=domain.js.map